import React, { useCallback, useEffect, useRef, useState } from "react";

const STEP = 4;
const STEP_SHIFT = 20;

export function SliderPuzzle({
  image,
  piece,
  pieceY = 0,
  width = 280,
  height = 160,
  pieceSize = 44,
  label = "Сдвиньте фрагмент на место",
  refreshLabel = "Обновить картинку",
  checkLabel = "Проверить",
  onCommit,
  onRefresh,
}) {
  const maxX = Math.max(0, width - pieceSize);
  const [x, setX] = useState(0);
  const startedRef = useRef(Date.now());
  const movesRef = useRef(0);
  const draggingRef = useRef(false);
  const originRef = useRef({ pointer: 0, x: 0 });

  useEffect(() => {
    setX(0);
    movesRef.current = 0;
    startedRef.current = Date.now();
  }, [image]);

  const pointerX = (event) => {
    const value = Number(event.clientX);
    return Number.isFinite(value) ? value : originRef.current.pointer;
  };

  const commit = useCallback(
    (nextX, nextMoves) => {
      if (onCommit) {
        onCommit({
          x: nextX,
          moves: nextMoves,
          ms: Date.now() - startedRef.current,
        });
      }
    },
    [onCommit]
  );

  const moveTo = useCallback(
    (next, shouldCommit) => {
      const clamped = Math.max(0, Math.min(maxX, Math.round(Number.isFinite(next) ? next : 0)));
      setX(clamped);
      movesRef.current += 1;
      if (shouldCommit) {
        commit(clamped, movesRef.current);
      }
      return clamped;
    },
    [commit, maxX]
  );

  const onPointerDown = (event) => {
    event.preventDefault();
    draggingRef.current = true;
    originRef.current = { pointer: pointerX(event), x };
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event) => {
    if (!draggingRef.current) {
      return;
    }
    const delta = pointerX(event) - originRef.current.pointer;
    const next = Math.max(0, Math.min(maxX, originRef.current.x + delta));
    setX(Math.round(next));
  };

  const onPointerUp = (event) => {
    if (!draggingRef.current) {
      return;
    }
    draggingRef.current = false;
    const delta = pointerX(event) - originRef.current.pointer;
    moveTo(originRef.current.x + delta, true);
  };

  const onKeyDown = (event) => {
    let step = 0;
    if (event.key === "ArrowRight") {
      step = event.shiftKey ? STEP_SHIFT : STEP;
    } else if (event.key === "ArrowLeft") {
      step = event.shiftKey ? -STEP_SHIFT : -STEP;
    } else if (event.key === "Enter" || event.key === " ") {
      // Keyboard users confirm explicitly: each arrow press must not be an attempt.
      event.preventDefault();
      commit(x, movesRef.current);
      return;
    } else {
      return;
    }
    event.preventDefault();
    moveTo(x + step, false);
  };

  return (
    <div className="af-puzzle">
      <div className="af-puzzle-label" id="af-puzzle-label">
        {label}
      </div>
      <div className="af-puzzle-board" style={{ width, height }}>
        <img className="af-puzzle-bg" src={image} alt="" width={width} height={height} />
        <div
          className="af-puzzle-piece"
          role="slider"
          tabIndex={0}
          aria-labelledby="af-puzzle-label"
          aria-valuemin={0}
          aria-valuemax={maxX}
          aria-valuenow={x}
          style={{
            width: pieceSize,
            height: pieceSize,
            top: pieceY,
            left: x,
            backgroundImage: `url(${piece})`,
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="af-puzzle-controls">
        <button type="button" className="af-puzzle-btn" onClick={() => moveTo(x - STEP, false)}>
          ←
        </button>
        <button type="button" className="af-puzzle-btn" onClick={() => moveTo(x + STEP, false)}>
          →
        </button>
        <button
          type="button"
          className="af-puzzle-check"
          onClick={() => commit(x, movesRef.current)}
        >
          {checkLabel}
        </button>
        {onRefresh ? (
          <button type="button" className="af-puzzle-refresh" onClick={onRefresh}>
            {refreshLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
